import { Map } from 'immutable';

import { sum } from '../helpers/Immutable';

import BaseStore from './BaseStore';
import { ActionTypes, DurationTypes } from '../constants/AppConstants';

import localStorageHelper from '../helpers/LocalStorage';
import RefundConfig from '../records/refund/Config';
import RefundRequest from '../records/refund/Request';
import UtnDetails from '../records/UtnDetails';

const STORAGE_KEY = 'refundConfig';
const CONFIG_TTL = 60 * 60;

export class RefundStore extends BaseStore {

    getInitialState () {
        const config = localStorageHelper.getItem(STORAGE_KEY);
        return new Map({
            config: config && RefundConfig.fromJSON(config),
            request: new RefundRequest(),
            order: null,
            calculations: null,
            utnDetails: new UtnDetails()
        });
    }

    handleConfig ({ config }) {
        localStorageHelper.setItem(STORAGE_KEY, config.toJS(), CONFIG_TTL);
        this.setState(this.state.set('config', config));
    }

    handleRefundDetails ({ order }) {
        const cardPayments = order.getCardPayments();
        const paypalPayment = order.getPaypalPayment();
        const googlePayment = order.getGooglePayPayment();
        const applePayment = order.getApplePayPayment();
        const braintreeCardPayment = order.getBraintreeCardPayment();

        let maxCashReturn;
        if (cardPayments && cardPayments.size > 0) {
            maxCashReturn = sum(cardPayments, 'amount');
        } else if (paypalPayment && paypalPayment.amount > 0) {
            maxCashReturn = paypalPayment.amount;
        } else if (googlePayment && googlePayment.amount > 0) {
            maxCashReturn = googlePayment.amount;
        } else if (applePayment && applePayment.amount > 0) {
            maxCashReturn = applePayment.amount;
        } else if (braintreeCardPayment && braintreeCardPayment.amount > 0) {
            maxCashReturn = braintreeCardPayment.amount;
        } else {
            maxCashReturn = 0;
        }

        let request = this.state.get('request')
            .set('maxCashReturn', maxCashReturn)
            .setNonRefundableVoucherAmount(order.getLightPromoPaymentAmount());

        if ((!cardPayments || cardPayments.size === 0) && !paypalPayment && !googlePayment && !applePayment && !braintreeCardPayment) {
            request = request.setVoucherAmount(request.total);
        }

        if (googlePayment) {
            request = request.setGooglePayAmount(googlePayment.amount);
        }

        if (applePayment) {
            request = request.setApplePayAmount(applePayment.amount);
        }

        if (braintreeCardPayment) {
            request = request.setBraintreeCardPaymentAmount(braintreeCardPayment.amount);
        }

        request = request.setBookingFee(order.getBookingFeePrice());

        this.setState(this.state.merge({
            order,
            request: order.alreadyPaidForRefund ? request.setFeePaid() : request
        }));
    }

    handleITSORefundCalculations ({ calculations }) {
        const order = this.get('order');
        let request = this.get('request');
        let fare;

        const trips = order.hasChangeoverTickets() ? order.getChangeoverTrips() : order.trips;
        const trip = trips.first();

        if (trip.hasSeasonFare()) {
            fare = trip.getSeasonFare();
        } else {
            fare = trip.getOutwardFare() || trip.getInwardFare();
        }

        const ticket = fare.tickets.first();

        const totalUsed = calculations.getIn([ 0, 'totalUsed']);
        const { reasons } = this.get('config');
        const itsoReasons = reasons.filter(rc => { return rc.itso; });

        if (fare && ticket) {
            request = request.setIsSmartcard()
                .setTotalUsed(order.isMultiSmartcard() ? 0 : totalUsed)
                .set('reason', totalUsed > 0 ?
                    itsoReasons.find(ir => ir.code === 7) : // Used
                    itsoReasons.find(ir => ir.code === 6)); // Not-Used
        }

        if (!fare.getOutwardValidityPeriod().matchDurationType(DurationTypes.ANNUAL)) { // If not annual then Weekly, Monthly or Custom
            request = request.setIsWMC();
        }

        if ((fare.origin.isWithinGoldcardArea() || fare.destination.isWithinGoldcardArea()) &&
            fare.getOutwardValidityPeriod().matchDurationType(DurationTypes.ANNUAL)) {
            request = request.setShouldSurrenderGoldCard();
        }

        if (order.hasVoucherPayment()) {
            request = request.setVoucherAmount();
        }

        request = request.setBookingFee(order.getBookingFeePrice());

        this.setState(this.state.merge({
            request,
            calculations
        }));
    }

    handleRefundCalculations ({ calculations }) {
        let request = this.get('request');

        this.setState(this.state.merge({
            request,
            calculations
        }));
    }

    handleUpdated ({ refundRequest, reasonChanged }) {
        let request = refundRequest;
        const { voucherAmount, minVoucherAmount } = request;

        if (reasonChanged) { request = request.clearFares(); }
        if (voucherAmount < minVoucherAmount) { request = request.set('voucherAmount', minVoucherAmount); }

        this.setState(this.state.set('request', request));
    }

    handleUtnDetails ({ utnDetails }) {
        this.setState(this.state.set('utnDetails', utnDetails));
    }

    getActionHandlers () {
        return {
            [ActionTypes.REFUND_CONFIG_LOADED]: this.handleConfig,
            [ActionTypes.REFUND_DETAILS_LOADED]: this.handleRefundDetails,
            [ActionTypes.REFUND_CALCULATIONS_LOADED]: this.handleRefundCalculations,
            [ActionTypes.REFUND_ITSO_CALCULATIONS_LOADED]: this.handleITSORefundCalculations,
            [ActionTypes.REFUND_UPDATED]: this.handleUpdated,
            [ActionTypes.REFUND_RESET]: this.resetState,
            [ActionTypes.UTN_DETAILS]: this.handleUtnDetails
        };
    }
}

const instance = new RefundStore();

export default instance;
